import React, { useMemo } from 'react';

import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Button, FormHelperText, FormLabel, IconButton, MenuItem, Stack, TextField } from '@mui/material';

import { getRoomBedTypeOptionsFromSchema, useRoomTypesSchema } from '~/hooks/apis/accommodation/useRoomTypesSchema';
import { useGenerateBeddingConfiguration } from '~/hooks/apis/offers/useGenerateBeddingConfiguration';

import { reportError } from '~/utils/reportError';

import type { OfferBuilderRoom } from '../types';
import { formatBedType } from '../utils';

interface Props {
  onChange: (nextRoomValues: Partial<OfferBuilderRoom>) => void;
  room: OfferBuilderRoom;
  syncBeddingConfigurationPackageId?: string;
}

type BedGroup = NonNullable<OfferBuilderRoom['bed_groups']>[number];
type BedConfiguration = NonNullable<BedGroup['configuration']>[number];

function parseOptionalNumber(value: string) {
  return value === '' ? null : Number(value);
}

function getDeclaredBedCount(room: OfferBuilderRoom) {
  if (Array.isArray(room.number_of_beds)) {
    return room.number_of_beds.reduce((total, count) => total + count, 0);
  }

  return room.number_of_beds ?? 0;
}

function buildDefaultBedGroups(room: OfferBuilderRoom): Array<BedGroup> {
  return [
    {
      id: 'main-bedroom',
      configuration: [
        {
          type: '',
          size: '',
          quantity: getDeclaredBedCount(room) || undefined,
        },
      ],
    },
  ];
}

function normaliseBedGroups(room: OfferBuilderRoom) {
  return room.bed_groups?.length ? room.bed_groups : buildDefaultBedGroups(room);
}

function getBedTypeOptions(bedGroups: Array<BedGroup>, schemaBedTypeOptions: Array<string>) {
  const currentBedTypes = bedGroups
    .flatMap((bedGroup) => bedGroup.configuration ?? [])
    .map((configuration) => getBedTypeValue(configuration.type, schemaBedTypeOptions));

  return Array.from(new Set(currentBedTypes.concat(schemaBedTypeOptions).filter(Boolean)));
}

function getBedTypeValue(type: string | undefined, schemaBedTypeOptions: Array<string>) {
  const formattedType = formatBedType(type);

  return schemaBedTypeOptions.find((bedType) => formatBedType(bedType) === formattedType) ?? type ?? '';
}

function getBedConfigurationRows(bedGroups: Array<BedGroup>) {
  return bedGroups.flatMap((bedGroup, bedGroupIndex) =>
    (bedGroup.configuration ?? []).map((configuration, configurationIndex) => ({
      bedGroup,
      bedGroupIndex,
      configuration,
      configurationIndex,
    })),
  );
}

function getNumberOfBeds(bedGroups: Array<BedGroup>) {
  return bedGroups.map((bedGroup) =>
    (bedGroup.configuration ?? []).reduce((total, configuration) => total + (configuration.quantity || 0), 0),
  );
}

export default function BedsAndSizeTab({ onChange, room, syncBeddingConfigurationPackageId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: roomTypesSchema } = useRoomTypesSchema({ propertyId: room.property_id });
  const generateBeddingConfigurationMutation = useGenerateBeddingConfiguration();
  const schemaBedTypeOptions = useMemo(() => getRoomBedTypeOptionsFromSchema(roomTypesSchema), [roomTypesSchema]);
  const bedGroups = normaliseBedGroups(room);
  const bedConfigurationRows = getBedConfigurationRows(bedGroups);
  const bedTypeOptions = getBedTypeOptions(bedGroups, schemaBedTypeOptions);
  const configuredBedCount = getNumberOfBeds(bedGroups).reduce((total, count) => total + count, 0);
  const declaredBedCount = getDeclaredBedCount(room) || configuredBedCount;
  const hasBedCountMismatch = declaredBedCount !== configuredBedCount;

  const updateBedGroups = (nextBedGroups: Array<BedGroup>, numberOfBedrooms = room.number_of_bedrooms) => {
    onChange({
      bed_groups: nextBedGroups,
      number_of_beds: getNumberOfBeds(nextBedGroups),
      number_of_bedrooms: numberOfBedrooms,
    });
  };

  const updateBedConfiguration = (
    bedGroupIndex: number,
    configurationIndex: number,
    nextConfiguration: BedConfiguration,
  ) => {
    updateBedGroups(
      bedGroups.map((bedGroup, currentBedGroupIndex) =>
        currentBedGroupIndex === bedGroupIndex
          ? {
              ...bedGroup,
              configuration: (bedGroup.configuration ?? []).map((configuration, currentConfigurationIndex) =>
                currentConfigurationIndex === configurationIndex ? nextConfiguration : configuration,
              ),
            }
          : bedGroup,
      ),
    );
  };

  const addBedConfiguration = () => {
    const nextConfiguration = { type: schemaBedTypeOptions[0] ?? '', size: '', quantity: 1 };
    const nextBedGroups = bedGroups.map((bedGroup, index) =>
      index === 0 ? { ...bedGroup, configuration: (bedGroup.configuration ?? []).concat(nextConfiguration) } : bedGroup,
    );

    updateBedGroups(nextBedGroups, Math.max(room.number_of_bedrooms ?? 0, nextBedGroups.length));
  };

  const deleteBedConfiguration = (bedGroupIndex: number, configurationIndex: number) => {
    const nextBedGroups = bedGroups
      .map((bedGroup, currentBedGroupIndex) =>
        currentBedGroupIndex === bedGroupIndex
          ? {
              ...bedGroup,
              configuration: (bedGroup.configuration ?? []).filter(
                (_configuration, currentConfigurationIndex) => currentConfigurationIndex !== configurationIndex,
              ),
            }
          : bedGroup,
      )
      .filter((bedGroup) => bedGroup.configuration?.length);

    updateBedGroups(nextBedGroups, nextBedGroups.length);
  };

  const syncBeddingConfiguration = async () => {
    if (!syncBeddingConfigurationPackageId) {
      enqueueSnackbar('No matching package found for this room type', { variant: 'warning' });
      return;
    }

    try {
      const response = await generateBeddingConfigurationMutation.mutateAsync(syncBeddingConfigurationPackageId);
      const syncedBedGroups = (response.result?.bed_grouping ?? []).map((bedGroup, index) => {
        const configuration = bedGroup.map((bedConfiguration) => ({
          quantity: bedConfiguration.quantity ?? 0,
          size: '',
          type: bedConfiguration.bed_type,
        }));

        return {
          id: `synced-bedroom-${index + 1}`,
          configuration,
        };
      });

      if (syncedBedGroups.length === 0) {
        enqueueSnackbar('No bedding configuration was generated for this room type', { variant: 'warning' });
        return;
      }

      onChange({
        bed_groups: syncedBedGroups,
        number_of_beds: getNumberOfBeds(syncedBedGroups),
        number_of_bedrooms: syncedBedGroups.length,
      });
      enqueueSnackbar(`Bedding configuration generated successfully for ${room.name || 'room'}`, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(
        `Failed to generate bedding configuration for ${room.name || 'room'}: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        { variant: 'error' },
      );
      reportError(error);
    }
  };

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        <Stack spacing={1}>
          <FormLabel>Size (m²)</FormLabel>
          <TextField
            type="number"
            size="small"
            value={room.size_sqm ?? ''}
            onChange={(event) => onChange({ size_sqm: parseOptionalNumber(event.target.value) })}
          />
        </Stack>
        <Stack spacing={1}>
          <FormLabel>Bedrooms</FormLabel>
          <TextField
            type="number"
            size="small"
            value={room.number_of_bedrooms ?? ''}
            onChange={(event) => onChange({ number_of_bedrooms: parseOptionalNumber(event.target.value) })}
          />
        </Stack>
        <Stack spacing={1}>
          <FormLabel>Bathrooms</FormLabel>
          <TextField
            type="number"
            size="small"
            value={room.number_of_bathrooms ?? ''}
            onChange={(event) => onChange({ number_of_bathrooms: parseOptionalNumber(event.target.value) })}
          />
        </Stack>
        <Stack spacing={1}>
          <FormLabel required>Number of beds</FormLabel>
          <TextField
            required
            type="number"
            size="small"
            value={declaredBedCount || ''}
            error={declaredBedCount <= 0 || hasBedCountMismatch}
            helperText={hasBedCountMismatch ? `Bed configuration total is ${configuredBedCount}` : undefined}
            onChange={(event) => onChange({ number_of_beds: [Number(event.target.value) || 0] })}
          />
        </Stack>
      </Box>

      <Stack spacing={1.5}>
        <FormLabel required>Bed configuration</FormLabel>
        {bedConfigurationRows.map(({ bedGroup, bedGroupIndex, configuration, configurationIndex }) => {
          const isBedConfigurationMissing = !configuration.type?.trim();

          return (
            <Stack key={`${bedGroup.id || bedGroupIndex}-${configurationIndex}`} spacing={0.5}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr) 92px 36px', md: 'minmax(0, 1fr) 120px 36px' },
                  gap: 1.5,
                  alignItems: 'center',
                }}
              >
                <TextField
                  select
                  size="small"
                  required
                  value={getBedTypeValue(configuration.type, schemaBedTypeOptions)}
                  error={isBedConfigurationMissing}
                  onChange={(event) =>
                    updateBedConfiguration(bedGroupIndex, configurationIndex, {
                      ...configuration,
                      type: event.target.value,
                    })
                  }
                >
                  <MenuItem value="" sx={{ display: 'none' }} />
                  {bedTypeOptions.map((bedType) => (
                    <MenuItem key={bedType} value={bedType}>
                      {formatBedType(bedType)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  type="number"
                  size="small"
                  inputProps={{ min: 0 }}
                  value={configuration.quantity ?? ''}
                  onChange={(event) =>
                    updateBedConfiguration(bedGroupIndex, configurationIndex, {
                      ...configuration,
                      quantity: Number(event.target.value) || 0,
                    })
                  }
                />
                <IconButton
                  color="error"
                  aria-label="Delete bed configuration"
                  disabled={bedConfigurationRows.length <= 1}
                  onClick={() => deleteBedConfiguration(bedGroupIndex, configurationIndex)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              {isBedConfigurationMissing && <FormHelperText error>Bed configuration is required</FormHelperText>}
            </Stack>
          );
        })}
        <Box>
          <Button variant="text" startIcon={<AddIcon />} onClick={addBedConfiguration}>
            Add bed
          </Button>
        </Box>
      </Stack>

      <Box>
        <Button
          variant="outlined"
          startIcon={<SyncIcon />}
          disabled={generateBeddingConfigurationMutation.isPending}
          onClick={syncBeddingConfiguration}
        >
          {generateBeddingConfigurationMutation.isPending ? 'Syncing...' : 'Sync bedding configuration'}
        </Button>
      </Box>
    </Stack>
  );
}
